import { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check, X, Star, ArrowRight, ExternalLink, TrendingUp, Home, LayoutGrid, FileText, Search, Wand2 } from "lucide-react";
import { tiers, packages, featureRows, xeroAddOn } from "@/data/pricing";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";

const formatR = (n) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n);

function cellValue(value) {
    if (value === true) return <Check className="w-4 h-4 text-tca-orange" />;
    if (value === false) return <X className="w-4 h-4 text-tca-line" />;
    return <span className="text-sm">{value}</span>;
}

export default function Pricing() {
    const [view, setView] = useState("chooser"); // "chooser" | "personal" | "company"
    const [tierId, setTierId] = useState("starter");
    const [billing, setBilling] = useState("monthly"); // monthly | annual
    const location = useLocation();

    // If navigated here with openView state (e.g. from "Personal Tax Returns" service card)
    useEffect(() => {
        if (location.state?.openView) {
            setView(location.state.openView);
        }
    }, [location.state]);

    const tier = useMemo(() => tiers.find((t) => t.id === tierId), [tierId]);
    const list = packages[tierId];

    return (
        <div data-testid="page-pricing">
            {/* Hero */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-24">
                    <div className="max-w-3xl animate-fade-up">
                        <p className="overline text-tca-orange mb-4">Pricing &amp; Packages · Est. 2015 · CA(SA) · RA</p>

                        {/* Heading — changes per view */}
                        {view === "company" ? (
                            <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                                Transparent retainers.
                                <span className="block text-tca-orange">No surprises.</span>
                            </h1>
                        ) : (
                            <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                                Transparent pricing.
                                <span className="block text-tca-orange">No surprises.</span>
                                <span className="block">Clear timelines.</span>
                            </h1>
                        )}

                        {/* Selector buttons — stacked above spiral */}
                        <div className="mt-10 flex flex-col items-start gap-3" data-testid="pricing-view-toggle">

                            {/* Two prominent choice buttons */}
                            <div
                                className="flex flex-col sm:flex-row border-2 border-tca-ink relative overflow-hidden"
                                style={view === "chooser" ? { animation: "tca-btn-glow 2s ease-in-out infinite" } : undefined}
                            >
                                {view === "chooser" && (
                                    <div
                                        aria-hidden="true"
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            background: "linear-gradient(105deg, transparent 35%, rgba(242, 107, 31, 0.22) 50%, transparent 65%)",
                                            animation: "tca-btn-shimmer 5s ease-in-out infinite",
                                        }}
                                    />
                                )}
                                <button
                                    onClick={() => setView("personal")}
                                    data-testid="pricing-btn-personal"
                                    className={`px-9 py-6 text-base font-extrabold uppercase tracking-[0.18em] transition-colors border-b-2 sm:border-b-0 sm:border-r-2 border-tca-ink/25 ${
                                        view === "personal"
                                            ? "bg-tca-ink text-white"
                                            : "bg-white text-tca-ink hover:bg-tca-orange hover:text-white hover:border-tca-orange"
                                    }`}
                                >
                                    Personal Taxes
                                </button>
                                <button
                                    onClick={() => setView("company")}
                                    data-testid="pricing-btn-company"
                                    className={`px-9 py-6 text-base font-extrabold uppercase tracking-[0.18em] transition-colors ${
                                        view === "company"
                                            ? "bg-tca-ink text-white"
                                            : "bg-white text-tca-ink hover:bg-tca-orange hover:text-white"
                                    }`}
                                >
                                    Company Accounting
                                </button>
                            </div>

                            {/* Curly spiral — below buttons, arrowhead points UP toward buttons */}
                            {view === "chooser" && (
                                <div
                                    className="flex flex-col items-center gap-2 select-none pointer-events-none"
                                    data-testid="chooser-hint"
                                >
                                    {/* Wrapper sized for the rotated SVG (160×110 rotated 90° → 110×160) */}
                                    <div style={{ width: 110, height: 160, position: "relative" }}>
                                        <svg
                                            width="160"
                                            height="110"
                                            viewBox="0 0 160 110"
                                            fill="none"
                                            className="text-tca-orange overflow-visible"
                                            aria-hidden="true"
                                            style={{
                                                position: "absolute",
                                                left: "50%",
                                                top: "50%",
                                                transform: "translate(-50%, -50%) rotate(90deg)",
                                            }}
                                        >
                                            {/* Spiral body */}
                                            <path
                                                d="M 152 55 C 152 8, 90 8, 90 55 C 90 102, 28 102, 28 55 C 28 15, 78 15, 78 55 C 78 80, 68 55, 62 55"
                                                stroke="currentColor"
                                                strokeWidth="3.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{
                                                    strokeDasharray: 400,
                                                    animation: "tca-spiral-draw 2.6s ease-in-out infinite",
                                                }}
                                            />
                                            {/* Arrowhead: barb bases straddle shaft end, tip points LEFT in SVG = UP after rotation */}
                                            <path
                                                d="M 62 42 L 44 55 L 62 68"
                                                stroke="currentColor"
                                                strokeWidth="3.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                style={{ animation: "tca-spiral-tip 2.6s ease-in-out infinite" }}
                                            />
                                        </svg>
                                    </div>
                                    <p
                                        className="text-tca-orange text-xs font-bold uppercase tracking-[0.22em] whitespace-nowrap"
                                        style={{ animation: "tca-spiral-label 1.6s ease-in-out infinite alternate" }}
                                    >
                                        Select a service
                                    </p>
                                </div>
                            )}
                        </div>
                        {view === "company" && (
                            <>
                                <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                                    Running a business means wearing too many hats — and tax compliance, VAT submissions, payroll and annual financial statements should not be one of them. An <strong className="text-tca-ink underline">accounting retainer</strong> is a single, <strong className="text-tca-ink underline">fixed</strong> monthly fee that puts a team of qualified accountants permanently in your corner: no surprise invoices, no scrambling at year-end, and no uncertainty about whether your numbers are right. You know exactly what you pay, exactly what is covered, and exactly who is responsible.
                                </p>
                                <p className="mt-4 text-tca-mute text-lg leading-relaxed max-w-2xl">
                                    While you focus on growing your business, our CA(SA)-led team handles every return, every deadline and every SARS query — giving you the <strong className="text-tca-ink underline">peace of mind</strong> that your compliance and tax affairs are in the hands of professionals who genuinely care about your bottom line.
                                </p>
                            </>
                        )}
                        {view === "company" && (
                            <p className="mt-4 text-tca-mute text-lg leading-relaxed max-w-2xl">
                                Nine fixed-fee packages across three tiers — Starter, Core and Elite — so you know exactly what compliance, accounting and advisory you are getting. All prices exclude the Xero subscription.
                            </p>
                        )}

                        {view === "company" && (
                            <div
                                data-testid="package-matchmaker-cta"
                                className="mt-8 max-w-2xl border-2 border-tca-ink bg-white p-6 sm:p-7 relative overflow-hidden"
                            >
                                <div
                                    aria-hidden="true"
                                    className="absolute inset-0 pointer-events-none opacity-[0.07]"
                                    style={{
                                        background:
                                            "radial-gradient(circle at 85% 0%, #F26B1F 0%, transparent 55%)",
                                    }}
                                />
                                <p className="overline text-tca-orange mb-3 inline-flex items-center gap-2">
                                    <Wand2 className="w-3.5 h-3.5" /> Package Matchmaker
                                </p>
                                <h3 className="font-display font-extrabold text-tca-ink text-2xl sm:text-3xl leading-tight">
                                    Tables and toggles too much detail?
                                </h3>
                                <p className="mt-3 text-tca-mute leading-relaxed">
                                    Skip the side-by-side comparison. Answer a handful of quick questions and we'll
                                    pinpoint the single package that fits how your business runs — VAT status, audit
                                    needs, payroll size and how hands-on you want to be.
                                </p>
                                <a
                                    href="/pricing/matchmaker"
                                    data-testid="package-matchmaker-btn"
                                    className="btn-primary group mt-6 relative"
                                >
                                    Help me choose which Package
                                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                </a>
                            </div>
                        )}

                        {/* Personal taxes content */}
                        {view === "personal" && (
                            <div className="mt-10 max-w-5xl space-y-14" data-testid="personal-taxes-content">

                                {/* 1 — Value Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    {[
                                        {
                                            icon: TrendingUp,
                                            title: "Maximise Your Refund",
                                            body: "Personal tax is an opportunity to recover money that is rightfully yours. We work through every available deduction avenue to improve your tax efficiency.",
                                        },
                                        {
                                            icon: Home,
                                            title: "Home Office Specialists",
                                            body: "We guide you through every qualifying criterion and supporting document required for home office claims to achieve the best possible outcome.",
                                        },
                                        {
                                            icon: LayoutGrid,
                                            title: "Packages for Every Need",
                                            body: "From simple pensioner submissions to complex sideline business returns — six packages designed to suit your exact circumstances.",
                                        },
                                    ].map(({ icon: Icon, title, body }) => (
                                        <div key={title} className="bg-white border border-tca-line p-6 flex flex-col gap-3">
                                            <div className="w-10 h-10 bg-tca-orange/10 flex items-center justify-center shrink-0">
                                                <Icon className="w-5 h-5 text-tca-orange" />
                                            </div>
                                            <p className="font-display font-bold text-tca-ink text-lg leading-tight">{title}</p>
                                            <p className="text-sm text-tca-mute leading-relaxed">{body}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* 2 — Deduction Checklist */}
                                <div>
                                    <p className="overline text-tca-orange mb-4">Deduction Opportunities We Review</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {[
                                            "Home Office Expenses",
                                            "Sideline Business Costs",
                                            "Independent Contractor Deductions",
                                            "Medical Aid & Out-of-Pocket Expenses",
                                            "Retirement Annuity Contributions",
                                            "Travel Allowance & Logbook",
                                        ].map((item) => (
                                            <div key={item} className="flex items-center gap-3 bg-white border border-tca-line px-4 py-3">
                                                <Check className="w-4 h-4 text-tca-orange shrink-0" />
                                                <span className="text-sm font-semibold text-tca-ink">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 3 — Process Timeline */}
                                <div>
                                    <p className="overline text-tca-orange mb-6">How We Work</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-0">
                                        {[
                                            {
                                                icon: FileText,
                                                step: "01",
                                                title: "12 Months of Financial Activity",
                                                body: "We gather and review your full year's financial records — payslips, statements, receipts and contracts.",
                                            },
                                            {
                                                icon: Search,
                                                step: "02",
                                                title: "Meticulous Analysis",
                                                body: "Every qualifying criterion and supporting document is examined to identify and validate every legitimate deduction.",
                                            },
                                            {
                                                icon: TrendingUp,
                                                step: "03",
                                                title: "Optimised Outcome",
                                                body: "Maximised tax efficiency and the best possible refund, with full SARS compliance and clear timelines.",
                                            },
                                        ].map(({ icon: Icon, step, title, body }, i) => (
                                            <div key={step} className={`relative bg-white border border-tca-line p-6 flex flex-col gap-3 ${i < 2 ? "sm:border-r-0" : ""}`}>
                                                <div className="flex items-center gap-3">
                                                    <span className="font-display font-extrabold text-3xl text-tca-orange/20 leading-none">{step}</span>
                                                    <div className="w-9 h-9 bg-tca-orange flex items-center justify-center shrink-0">
                                                        <Icon className="w-4 h-4 text-white" />
                                                    </div>
                                                </div>
                                                <p className="font-display font-bold text-tca-ink leading-tight">{title}</p>
                                                <p className="text-sm text-tca-mute leading-relaxed">{body}</p>
                                                {i < 2 && (
                                                    <ArrowRight className="hidden sm:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tca-orange z-10 bg-white" />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* CTA */}
                                <a
                                    href="https://tax.tcauditors.co.za"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="personal-taxes-cta"
                                    className="btn-primary inline-flex group"
                                >
                                    See pricing and options
                                    <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Company accounting — only shown when view === "company" */}
            {view === "company" && (
                <>
                    {/* Tier toggle + billing toggle */}
                    <section className="bg-white">
                        <div className="container-tca pt-12 md:pt-16">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-10">
                                {/* Tier toggle */}
                                <div
                                    data-testid="tier-toggle"
                                    className="inline-flex p-1.5 border border-tca-line bg-tca-fog rounded-none"
                                    role="tablist"
                                >
                                    {tiers.map((t) => (
                                        <button
                                            key={t.id}
                                            role="tab"
                                            aria-selected={tierId === t.id}
                                            data-testid={`tier-btn-${t.id}`}
                                            onClick={() => setTierId(t.id)}
                                            className={`px-5 sm:px-7 py-3 text-sm font-bold uppercase tracking-[0.18em] transition-all ${
                                                tierId === t.id
                                                    ? "bg-tca-ink text-white"
                                                    : "text-tca-ink hover:text-tca-orange"
                                            }`}
                                        >
                                            {t.name}
                                        </button>
                                    ))}
                                </div>

                                {/* Billing toggle */}
                                <div
                                    data-testid="billing-toggle"
                                    className="inline-flex p-1 border border-tca-line bg-white rounded-none"
                                >
                                    {[
                                        ["monthly", "Monthly"],
                                        ["annual", "Annual"],
                                    ].map(([k, label]) => (
                                        <button
                                            key={k}
                                            onClick={() => setBilling(k)}
                                            data-testid={`billing-btn-${k}`}
                                            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] transition-all ${
                                                billing === k
                                                    ? "bg-tca-orange text-white"
                                                    : "text-tca-ink hover:text-tca-orange"
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Tier description */}
                            <div
                                data-testid="tier-description"
                                className="border-l-4 border-tca-orange bg-tca-orangeSoft/40 px-6 py-5 mb-12 max-w-3xl"
                            >
                                <p className="text-xs uppercase tracking-[0.2em] font-bold text-tca-orange mb-1.5">{tier.tagline}</p>
                                <h3 className="font-display font-bold text-tca-ink text-xl mb-2">
                                    {tier.name} — {tier.statementType} Annual Financial Statements
                                </h3>
                                <p className="text-sm text-tca-mute leading-relaxed">
                                    {tier.description}
                                    {tier.descriptionExtra && (
                                        <> <strong className="text-tca-ink underline">{tier.descriptionExtra}</strong></>
                                    )}
                                </p>
                            </div>

                            {/* Package cards */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="package-grid">
                                {list.map((p) => (
                                    <article
                                        key={p.name}
                                        data-testid={`package-card-${tierId}-${p.name.toLowerCase()}`}
                                        className={`relative border ${
                                            p.highlight
                                                ? "border-tca-orange shadow-[0_0_0_2px_#F26B1F] bg-white"
                                                : "border-tca-line bg-white"
                                        } p-8 lg:p-10 lift`}
                                    >
                                        {p.highlight && (
                                            <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-tca-orange text-white px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold">
                                                <Star className="w-3 h-3 fill-current" /> Most popular
                                            </span>
                                        )}
                                        <p className="overline mb-3">{tier.name}</p>
                                        <h3 className="font-display font-extrabold text-tca-ink text-3xl mb-2">{p.name}</h3>
                                        <p className="text-sm text-tca-mute mb-7 min-h-[40px]">{p.blurb}</p>

                                        <div className="mb-7">
                                            <p className="font-display font-extrabold text-tca-ink text-5xl tracking-tight tabular-nums">
                                                {billing === "monthly" ? formatR(p.monthly) : formatR(p.annual)}
                                            </p>
                                            <p className="text-xs text-tca-mute mt-1">
                                                {billing === "monthly" ? "per month + Xero" : "per year + Xero"}
                                            </p>
                                        </div>

                                        <ul className="space-y-2.5 mb-8">
                                            {[
                                                ["VAT-registered", p.features.vatRegistered ? "Yes" : "No"],
                                                ["Financial Statements", p.features.statementType],
                                                ["Company Tax Returns", <Check className="w-4 h-4 text-tca-orange" />],
                                                ["Specialist", p.features.specialist],
                                                ["Workload", p.features.workSplit],
                                                ["Payroll", p.features.payroll],
                                                ["Advisory Meetings", p.features.discussionMeetings],
                                                ["Management Accounts", p.features.managementAccounts],
                                            ].map(([k, v]) => (
                                                <li key={k} className="flex items-center justify-between text-sm border-b border-tca-line pb-2">
                                                    <span className="text-tca-mute shrink-0">{k}</span>
                                                    <span className="text-tca-ink font-semibold text-right ml-2">{v}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <a
                                            href={`https://retainer.tcauditors.co.za/${tierId}-${p.name.toLowerCase()}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`package-cta-${tierId}-${p.name.toLowerCase()}`}
                                            className={`w-full ${p.highlight ? "btn-primary" : "btn-secondary"} group`}
                                        >
                                            Get {tier.name} {p.name}
                                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                        </a>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Detailed comparison table */}
                    <section className="bg-tca-fog border-y border-tca-line mt-20">
                        <div className="container-tca py-16 md:py-20">
                            <div className="mb-10 max-w-2xl">
                                <p className="overline mb-3">Side-by-side</p>
                                <h2 className="font-display font-extrabold text-tca-ink text-3xl sm:text-4xl leading-tight">
                                    Every feature, lined up.
                                </h2>
                                <p className="mt-3 text-tca-mute">{tier.name} tier — Bronze, Silver and Gold.</p>
                            </div>

                            <div className="overflow-x-auto bg-white border border-tca-line">
                                <table className="w-full text-sm" data-testid="comparison-table">
                                    <thead>
                                        <tr className="bg-tca-ink text-white">
                                            <th className="text-left px-5 py-4 font-bold uppercase tracking-[0.16em] text-xs">
                                                Included in {tier.name}
                                            </th>
                                            {list.map((p) => (
                                                <th
                                                    key={p.name}
                                                    className="text-center px-5 py-4 font-bold uppercase tracking-[0.16em] text-xs"
                                                >
                                                    {p.name}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            let dataIdx = 0;
                                            return featureRows.map((row) => {
                                                if (row.type === "header") {
                                                    return (
                                                        <tr key={`section-${row.label}`}>
                                                            <td
                                                                colSpan={list.length + 1}
                                                                className="px-5 py-2 bg-tca-orangeSoft border-t border-orange-100 text-tca-orange font-bold text-[11px] uppercase tracking-[0.2em]"
                                                            >
                                                                {row.label}
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                const bg = dataIdx++ % 2 === 0 ? "bg-white" : "bg-tca-fog";
                                                return (
                                                    <tr key={row.key} className={bg}>
                                                        <td className="px-5 py-3.5 font-medium text-tca-ink border-r border-tca-line">
                                                            {row.label}
                                                        </td>
                                                        {list.map((p) => (
                                                            <td key={p.name} className="px-5 py-3.5 text-center">
                                                                <div className="flex items-center justify-center">
                                                                    {cellValue(p.features[row.key])}
                                                                </div>
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            });
                                        })()}
                                        <tr className="bg-tca-ink text-white">
                                            <td className="px-5 py-4 font-bold uppercase text-xs tracking-[0.16em]">Monthly</td>
                                            {list.map((p) => (
                                                <td key={p.name} className="px-5 py-4 text-center font-display font-bold">
                                                    {formatR(p.monthly)}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr className="bg-tca-orange text-white">
                                            <td className="px-5 py-4 font-bold uppercase text-xs tracking-[0.16em]">Annual</td>
                                            {list.map((p) => (
                                                <td key={p.name} className="px-5 py-4 text-center font-display font-bold">
                                                    {formatR(p.annual)}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Xero add-on */}
                            <div className="mt-8 border border-tca-line bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="xero-addon">
                                <div>
                                    <p className="overline mb-2 text-tca-orange">Required add-on</p>
                                    <h3 className="font-display font-bold text-xl text-tca-ink">{xeroAddOn.name}</h3>
                                    <p className="text-sm text-tca-mute mt-1 max-w-2xl">{xeroAddOn.note}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-display font-extrabold text-3xl text-tca-ink">{formatR(xeroAddOn.monthly)}</p>
                                    <p className="text-xs text-tca-mute">per month · {formatR(xeroAddOn.annual)}/year</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <TrustBar />

                    {/* CTA */}
                    <section className="bg-white">
                        <div className="container-tca py-16 max-w-2xl">
                            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-tca-ink leading-[1.05]">Not sure which package fits?</h2>
                            <a
                                href="/pricing/matchmaker"
                                data-testid="pricing-bottom-matchmaker-btn"
                                className="btn-primary group mt-6 inline-flex relative"
                            >
                                Help me choose which Package
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </a>
                            <p className="mt-8 text-tca-mute">Send a quick inquiry — or book a 30-minute virtual meeting and we will recommend the right tier in real time.</p>
                            <div className="mt-4">
                                <CTAButtons testIdPrefix="pricing-cta" />
                            </div>
                        </div>
                    </section>
                </>
            )}

            {/* TrustBar for non-company views */}
            {view !== "company" && <TrustBar />}
        </div>
    );
}
